
import GenericNavigation from './GenericNavigation';
import Main from './Main';
import About from './About';
import Expertise from './Expertise';
import Project from './Project';
import Contact from './Contact';
import { ToastProvider } from './ToastProvider.js';

import { useState } from 'react';
import MarkdownComponent from './MarkdownComponent';

const paragraphs = ["iOS app development is the craft of building apps for Apple devices—iPhone and iPad—on the iOS platform. Developers primarily use Swift, with Objective-C or C++ for legacy or performance-critical modules, and build in Xcode (with Swift Playgrounds for quick prototypes). Great iOS apps blend strong engineering with product sense: intuitive UX, smooth performance, efficient battery use, and alignment with Apple’s design and App Store guidelines."
]


function IOSAppDevelopment() {
  const [mode, setMode] = useState<'light' | 'dark'>('light');
  const handleModeChange = () => {
    setMode((prev) => (prev === 'dark' ? 'light' : 'dark'));
  };
  const iosNavItems: [string, string][] = [
    ["Home", 'home'],
    ['About iOS', 'about-ios'],
    ['Services', 'services'],
    ['Why Us', 'why-us'],
    ['Expertise', 'expertise'],
    ['Contact Us', 'contact-us'],
  ];
  const iosServicesOptions = [
    'iOS app development',
    'Android app development',
    'Mobile app development',
    'AI-Centric Development',
  ];

  const whyUsMd = `
# Why Us

We don’t just “build screens.” We take products from *zero to one—owning outcomes, speed, and quality end-to-end. That’s exactly how we built **RepCard SaaS* from an idea into a production mobile platform used by sales teams.

## What we did for RepCard — 0 → 1, fast

•⁠  ⁠*Shaped the product* from a blank slate: discovery sprint, backlog, user flows, and a lean MVP scope that could actually ship.\n
•⁠  ⁠*Engineered the core app* with clean, testable architecture (iOS & Android) and release-ready CI/CD.\n
•⁠  ⁠*Delivered the must-have features*:

  • Real-time *Chat* (typing indicators, media, push, deep links)\n
  • *Leaderboards & Competitions* (live ranks, server-driven rules)\n
  • *Calendar* for setter/closer scheduling with reminders\n
  • *Digital Business Card* (share, QR, contact actions)\n
  • *Canvassing Map* with tiled overlays to handle heavy geo data smoothly\n
  • *Custom Fields* so ops could capture business-specific data without new releases \n
•⁠  ⁠*Hardened performance & reliability*: Instruments/Profiler runs, main-thread audits, memory fixes, staged rollouts, and release health dashboards.\n
•⁠  ⁠*Operationalized quality*: feature flags, crash/ANR gates, weekly demo cadence, and docs/runbooks for handover.

## Outcomes we’re proud of

•⁠  ⁠*Shipped an MVP in weeks*, then iterated to a multi-feature product with steady velocity.\n
•⁠  ⁠*Crash-free sessions >99%* on maintained releases and *snappier startup* from targeted performance work.\n
•⁠  ⁠*Battery & network efficiency* for field users working on poor connectivity.\n
•⁠  ⁠*Predictable releases* with CI, TestFlight/Play tracks, and data-driven go/no-go gates.

## Why that matters for you

•⁠  ⁠*Proven 0→1 playbook*: from fuzzy idea to a live app customers use.\n
•⁠  ⁠*Speed without chaos*: small, senior pods; weekly demos; visible metrics.\n
•⁠  ⁠*Maintainability baked in*: modular code, tests, and clear ownership.\n
•⁠  ⁠*Outcome-first mindset*: we align to your business KPIs—not vanity deliverables.\n
•⁠  ⁠*Long-term partnership*: we don’t just build and leave; we’re here to grow with you.
`;

const expertiseMd = `
# Expertise

•⁠ Swift, SwiftUI, UIKit, Combine, Swift Concurrency\n
•⁠ Instruments (Time Profiler, Allocations), leak hunts, main-thread audits\n
•⁠ Push & in-app notifications, deep links, background tasks\n
•⁠ Secure storage (Keychain), encryption, privacy prompts (ATT)\n
•⁠ MapKit at scale (e.g., MKTileOverlay for canvassing in RepCard)\n
•⁠ Modular codebases, SPM/CocoaPods, SDK design (incl. iOS 12 compatibility)\n
•⁠ XCTest/XCUITest, snapshot tests, CI/CD with Fastlane & TestFlight

`;

const achievementsMd = `
# Achievements

•⁠ Shipped RepCard iOS chat with typing indicators, media sharing, and push delivery.\n
•⁠ Delivered Leaderboards & Competitions with real-time updates and server-driven rules.\n
•⁠ Implemented tile-based map rendering to handle heavy polygon datasets smoothly.\n
•⁠ Introduced crash & performance playbooks and a release-health dashboard.\n
•⁠ Automated releases with Fastlane, staged rollouts, and clear submission notes.\n
•⁠ Maintained >99% crash-free sessions on active releases.\n
•⁠ Cut app startup time and navigation jank through targeted main-thread fixes.\n
•⁠ Reduced battery impact of background tasks for field users on poor networks.\n
•⁠ Hit sprint commitments consistently with demo-ready builds every week.

`;

  return (
    <ToastProvider>
      <div className={`main-container ${mode === 'dark' ? 'dark-mode' : 'light-mode'}`}>
        <GenericNavigation
          navItems={iosNavItems}
          servicesOptions={iosServicesOptions}
          mode={mode}
          modeChange={handleModeChange}
          sectionIdMap={{
            'home': 'home',
            'about-ios': 'about-ios',
            'services': 'services',
            'why-us': 'why-us',
            'expertise': 'expertise',
            'contact-us': 'contact',
          }}
        />
        <Main />
        <About heading='About iOS' paragraphs={paragraphs} />
        <MarkdownComponent paragraph={whyUsMd}/>
        <MarkdownComponent paragraph={expertiseMd}/>
        <MarkdownComponent paragraph={achievementsMd}/>
        <Contact />
      </div>
    </ToastProvider>
  );
}

export default IOSAppDevelopment;
